import { ChevronDownIcon } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/components/ui/collapsible";

import { AssistantContentBuilder } from "./AssistantContentBuilder";
import { collapseRepeatedSteps } from "./collapse-repeated-steps";

import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";

type ThoughtProcessGroupProps = {
  parts: FlattenedPart[];
  isLastTurn?: boolean;
} & MessageFlags;

/**
 * The experience-agent "thought process" timeline: the turn's reasoning + tool
 * steps collapsed into one accordion. The header reads "Thought process…" while
 * the turn is still streaming and "Task completed" once it's done (done = not
 * the actively-streaming last turn). Each part renders as a clean step row (icon
 * + title + description) via `AssistantContentBuilder` — no connecting rule, so
 * it reads as a simple list. Consecutive identical steps collapse into one row
 * with a repeat badge (see `collapseRepeatedSteps`).
 */
export const ThoughtProcessGroup = ({
  parts,
  isLastTurn,
  ...flags
}: ThoughtProcessGroupProps) => {
  const inProgress = !!flags.isStreaming && !!isLastTurn;
  const title = inProgress ? "Thought process…" : "Task completed";

  return (
    <Collapsible defaultOpen className="mb-4 w-full">
      <CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-2 py-1 text-xs transition-colors">
        <span className="font-medium text-foreground">{title}</span>
        <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-1">
        {collapseRepeatedSteps(parts).map(({ part, repeatCount }, i) => (
          <AssistantContentBuilder
            key={`${part.nodeId}-${i}`}
            item={part}
            index={i}
            repeatCount={repeatCount}
            {...flags}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};
